import { clamp } from '../helpers'
import { state } from '../map_state/state'
import { UnitConverter } from '../unit_converter'
import { Renderer } from '../renderer'
import { rssiForRadius } from '../rssi'
import { addSensorTool } from '../tools/add_sensor_tool'
import { historyTool } from '../tools/history_tool'

export class Sensor {
  // Hard limits and defaults (meters) works only when map scale has been determined.
  static MAX_OUTER_M = 20
  static DEFAULT_INNER_M = 10
  static DEFAULT_OUTER_M = 20
  static DEFAULT_ZONE_A_COLOR = '#bf40bf'
  static DEFAULT_ZONE_B_COLOR = '#25db4b'

  constructor (attrs = {}) {
    Object.assign(this, attrs)

    this.flowEndSensorIds ??= []

    this.edgeFadePx ??= 18
    this.zoneAColor ??= Sensor.DEFAULT_ZONE_A_COLOR
    this.zoneBColor ??= Sensor.DEFAULT_ZONE_B_COLOR
    this.sensorColor ??= 'rgba(0,120,255,1)'

    this.enableZoneA ??= true
    this.enableZoneB ??= true

    this.visible ??= true
    this.engagedDuration ??= 60

    this.rssiA ??= -45
    this.rssiB ??= -45

    this.zoneARadiusElement = null
    this.zoneBRadiusElement = null
    this.zoneARssiElement = null
    this.zoneBRssiElement = null
    this.element = null

    if (state.calibration && state.lenUnit) {
      const to = state.lenUnit
      this.maxOuterRadius = UnitConverter.convert(Sensor.MAX_OUTER_M, 'm', to)
      this.defaultInnerRadius = UnitConverter.convert(Sensor.DEFAULT_INNER_M, 'm', to)
      this.defaultOuterRadius = UnitConverter.convert(Sensor.DEFAULT_OUTER_M, 'm', to)
    }

    this.zoneARadius ??= state.calibration ? this.defaultInnerRadius : 80
    this.zoneBRadius ??= state.calibration ? this.defaultOuterRadius : 160
    this.zoneRadiusUnit ??= state.calibration ? state.lenUnit : 'px'

    this.zoneAColor = this.normalizeToHex(this.zoneAColor, Sensor.DEFAULT_ZONE_A_COLOR)
    this.zoneBColor = this.normalizeToHex(this.zoneBColor, Sensor.DEFAULT_ZONE_B_COLOR)
    if (this.id != 'preview') this.saveElement()
  }

  normalizeToHex (value, fallback) {
    if (typeof value !== 'string') return fallback
    if (value.startsWith('#') && value.length === 7) return value
    const m = value.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i)
    if (!m) return fallback
    const r = Math.max(0, Math.min(255, Number(m[1]) | 0))
    const g = Math.max(0, Math.min(255, Number(m[2]) | 0))
    const b = Math.max(0, Math.min(255, Number(m[3]) | 0))
    return '#' + [r, g, b].map(n => n.toString(16).padStart(2, '0')).join('')
  }

  convertMeasurements () {
    if (!state.calibration || !state.lenUnit) return

    const to = state.lenUnit

    if (this.zoneRadiusUnit === 'px' || !this.zoneRadiusUnit) {
      this.maxOuterRadius = UnitConverter.convert(Sensor.MAX_OUTER_M, 'm', to)
      this.defaultInnerRadius = UnitConverter.convert(Sensor.DEFAULT_INNER_M, 'm', to)
      this.defaultOuterRadius = UnitConverter.convert(Sensor.DEFAULT_OUTER_M, 'm', to)

      this.zoneARadius = this.defaultInnerRadius
      this.zoneBRadius = this.defaultOuterRadius
      this.zoneRadiusUnit = to
      return
    }

    const from = this.zoneRadiusUnit
    if (from === to) return

    this.maxOuterRadius = UnitConverter.convert(this.maxOuterRadius, from, to)
    this.defaultInnerRadius = UnitConverter.convert(this.defaultInnerRadius, from, to)
    this.defaultOuterRadius = UnitConverter.convert(this.defaultOuterRadius, from, to)
    this.zoneARadius = UnitConverter.convert(this.zoneARadius, from, to)
    this.zoneBRadius = UnitConverter.convert(this.zoneBRadius, from, to)
    this.zoneRadiusUnit = to
  }

  ensureDefaults () {
    this.convertMeasurements()
    if (!state.calibration) {
      this.clamp(false)
      return
    }

    this.zoneARadius ??= this.defaultInnerRadius
    this.zoneBRadius ??= this.defaultOuterRadius
    this.clamp(true)
  }

  clamp (hasScale) {
    if (hasScale) {
      if (this.enableZoneB) {
        this.zoneBRadius = clamp(this.zoneBRadius, 0, this.maxOuterRadius)
        if (this.id != 'preview') this.setRanges(this.maxOuterRadius, 'B')
      }
      if (this.enableZoneA) {
        const aMax = this.enableZoneB ? this.zoneBRadius : this.maxOuterRadius
        this.zoneARadius = clamp(this.zoneARadius, 0, aMax)
        if (this.id != 'preview') this.setRanges(aMax, 'A')
      }
      this.setRssi()
      return
    }

    if (this.enableZoneB) this.zoneBRadius = Math.max(0, +this.zoneBRadius || 0)
    if (this.enableZoneA) {
      const aMax = this.enableZoneB ? (+this.zoneBRadius || 0) : Infinity
      this.zoneARadius = clamp(this.zoneARadius, 0, aMax)
      if (this.id != 'preview') this.setRanges(aMax, 'A')
    }
    this.setRssi()
  }

  setRanges (max, which) {
    const input = this[`zone${which}RadiusElement`]
    if (!input) return

    const value = Number(this[`zone${which}Radius`])
    input.max = Number(max)
    input.value = Number.isFinite(value) ? value.toFixed(1) : ''
  }

  resetZoneFields (updateValue = true) {
    this.maxOuterRadius = 160
    this.defaultInnerRadius = 80
    this.defaultOuterRadius = 160

    if (updateValue) {
      this.zoneARadius = this.defaultInnerRadius
      this.zoneBRadius = this.defaultOuterRadius
    }
    this.zoneRadiusUnit = 'px'

    if (this.zoneARadiusElement) {
      this.zoneARadiusElement.value = Number(this.zoneARadius).toFixed(1)
      this.zoneARadiusElement.max = Number(this.zoneBRadius)
    }

    if (this.zoneBRadiusElement) {
      this.zoneBRadiusElement.value = Number(this.zoneBRadius).toFixed(1)
      this.zoneBRadiusElement.max = ''
    }

    this.element?.querySelectorAll('.mct-zone-numerical-unit').forEach(el => {
      el.textContent = 'px'
    })
  }

  getZonePx () {
    this.convertMeasurements()

    if (state.calibration && this.zoneRadiusUnit !== 'px') {
      const scale = Number(state.lenPath)
      if (!Number.isFinite(scale) || scale <= 0) {
        return { a: null, b: null }
      }

      return {
        a: this.enableZoneA ? (Number(this.zoneARadius || 0) / scale) : null,
        b: this.enableZoneB ? (Number(this.zoneBRadius || 0) / scale) : null
      }
    }

    return {
      a: this.enableZoneA ? Number(this.zoneARadius || 0) : null,
      b: this.enableZoneB ? Number(this.zoneBRadius || 0) : null
    }
  }

  setZoneFromWorldPx (which, rWorldPx) {
    this.convertMeasurements()
    rWorldPx = Math.max(0, Number(rWorldPx) || 0)

    if (which === 'A' && !this.enableZoneA) return
    if (which === 'B' && !this.enableZoneB) return

    if (state.calibration && this.zoneRadiusUnit !== 'px') {
      const scale = Number(state.lenPath)
      if (!Number.isFinite(scale) || scale <= 0) return

      let r = rWorldPx * scale
      r = clamp(r, 0, Number(this.maxOuterRadius))

      this[`zone${which}Radius`] = r
      this.clamp(true)

      if (this[`zone${which}RadiusElement`]) {
        this[`zone${which}RadiusElement`].value = Number(this[`zone${which}Radius`]).toFixed(1)
      }

      this.setRssi()
      return
    }

    this[`zone${which}Radius`] = rWorldPx
    this.clamp(false)

    if (this[`zone${which}RadiusElement`]) {
      this[`zone${which}RadiusElement`].value = Number(this[`zone${which}Radius`]).toFixed(1)
    }

    this.setRssi()
  }

  setRssi () {
    if (state.calibration && this.id !== 'preview') {
      this.rssiA = rssiForRadius(this.zoneARadius ?? 0, this.zoneRadiusUnit)
      this.rssiB = rssiForRadius(this.zoneBRadius ?? 0, this.zoneRadiusUnit)

      if (this.zoneARssiElement) this.zoneARssiElement.value = this.rssiA
      if (this.zoneBRssiElement) this.zoneBRssiElement.value = this.rssiB
    }
  }

  saveElement () {
    const wrapper = document.createElement('div')
    wrapper.innerHTML = this.toHtml()

    const el = wrapper.firstElementChild
    this.element = el
    this.nameElement = el.querySelector('.mct-sensor-name')
    el.querySelectorAll('.mct-zone-radius').forEach(ele => this[`zone${ele.dataset.which}RadiusElement`] = ele)
    el.querySelectorAll('.mct-zone-rssi').forEach(ele => this[`zone${ele.dataset.which}RssiElement`] = ele)

    for (const which of ['A', 'B']) {
      this[`zone${which}RadiusElement`].addEventListener('change', function () {
        this[`zone${which}Radius`] = this[`zone${which}RadiusElement`].value
        this.ensureDefaults()
        Renderer.render()
        historyTool.add()
      }.bind(this))

      this[`zone${which}RadiusElement`].addEventListener('input', function () {
        if (document.activeElement === this[`zone${which}RadiusElement`]) return
        this[`zone${which}Radius`] = this[`zone${which}RadiusElement`].value
        this.ensureDefaults()
        Renderer.render()
      }.bind(this))

      this[`zone${which}RssiElement`].addEventListener('change', function () {
        this[`rssi${which}`] = this[`zone${which}RssiElement`].value
        Renderer.render()
        historyTool.add()
      }.bind(this))
    }

    el.addEventListener('click', function (event) {
      if (event.target.closest('.mct-sensor-remove')) this.remove()
      else this.select()
    }.bind(this))

    el.querySelector('.mct-sensor-name').addEventListener('change', function (event) {
      state.sensorNameChangedOnce = true
      this.name = event.target.value
      Renderer.render()
      historyTool.add()
    }.bind(this))

    el.querySelector('.mct-sensor-visible-input').addEventListener('change', function (event) {
      this.visible = event.target.checked
      Renderer.render()
      historyTool.add()
    }.bind(this))

    el.querySelector('.mct-engaged-duration-input').addEventListener('change', function (event) {
      this.engagedDuration = event.target.value
      historyTool.add()
    }.bind(this))

    el.querySelectorAll('.mct-zone-enable').forEach(ele => ele.addEventListener('change', function (event, target = ele) {
      this[`enableZone${target.dataset.which}`] = target.checked
      this.ensureDefaults()
      Renderer.render()
      historyTool.add()
    }.bind(this)))

    el.querySelectorAll('.mct-zone-color').forEach(ele => ele.addEventListener('input', function (event, target = ele) {
      this[`zone${target.dataset.which}Color`] = target.value
      target.closest('.mct-zone-range-container').style.setProperty('--bg-color', target.value)
      Renderer.render()
    }.bind(this)))

    el.querySelectorAll('.mct-zone-color').forEach(ele => ele.addEventListener('change', function () {
      historyTool.add()
    }))

    Sensor.updateNames()
  }

  enableRanges () {
    if (state.calibration) {
      this.zoneARadiusElement.disabled = false
      this.zoneBRadiusElement.disabled = false
    }
  }

  select () {
    document.querySelector('.mct-sensor.selected')?.classList?.remove('selected')
    this.element.classList.add('selected')
    state.selectedSensorId = this.id
    Renderer.render()
  }

  remove () {
    state.removeSensorEverywhere(this.id)
    state.sensors = state.sensors.filter(ele => ele.id != this.id)

    state.sensors.forEach(sensor => {
      sensor.flowEndSensorIds = (sensor.flowEndSensorIds || []).filter(
        id => String(id) !== String(this.id)
      )
    })

    if (String(state.flowStartSensorId) === String(this.id)) {
      state.flowStartSensorId = null
      state.flowPreviewWorld = null
    }

    Sensor.updateNames()
    this.element.remove()
    historyTool.add()
    Renderer.render()
  }

  toHistory () {
    const data = { ...this }
    data.flowEndSensorIds = [...data.flowEndSensorIds]
    data.position = { ...data.position }
    return data
  }

  static fromHistory (data) {
    const sensor = new Sensor({
      ...data,
      position: data.position ? { ...data.position } : { x: 0, y: 0 },
      flowEndSensorIds: [...(data.flowEndSensorIds || [])]
    })
    addSensorTool.tab.appendChild(sensor.element)
    Sensor.updateNames()
    return sensor
  }

  static updateNames () {
    if (!state.sensorNameChangedOnce) {
      state.sensors.forEach((ele, index) => {
        ele.name = `Sensor ${index + 1}`
        ele.nameElement.value = `Sensor ${index + 1}`
      })
    }
  }

  toHtml () {
    return `
        <div class="mct-sensor text-mono" data-sensor-id="${this.id}">
            <div class="mct-sensor-header" id="mct-sensor-header">
                <input class="mct-sensor-name" type="text" value="${this.name}">
                <div class="mct-sensor-remove">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
                        <path d="M512 64a448 448 0 1 1 0 896 448 448 0 0 1 0-896zM288 512a38.4 38.4 0 0 0 38.4 38.4h371.2a38.4 38.4 0 0 0 0-76.8H326.4A38.4 38.4 0 0 0 288 512z" fill="currentColor"></path>
                    </svg>
                </div>
            </div>

            <div class="mct-sensor-zone">
              <label for="visible-${this.id}" class="mct-sensor-visible"><input class="mct-sensor-visible-input" id="visible-${this.id}" type="checkbox" ${this.visible && 'checked'} hidden></label>
              <label for="engaged-duration-${this.id}" data-controller="drag-number" class="mct-zone-field-label mct-engaged-duration">
                <div class="mct-zone-field-name">ENGAGED DURATION (<span class="mct-duration-unit">s</span>) </div>
                <input class="mct-engaged-duration-input" id="engaged-duration-${this.id}" step="1" min="20" max="300" type="number" value="${this.engagedDuration}">
              </label>
              <div class="mct-zone-range-container" style="--bg-color:${this.zoneAColor};">
                <div class="mct-sensor-zone-header">
                  <label class="mct-zone-name" for="enabledZoneA-${this.id}" style="font-size: 9px;">
                    <span class="mct-zone-name-span" style="display: block;margin-bottom: 4px;">Max Engaged Signal Strength
                      <input id="enabledZoneA-${this.id}" class="mct-zone-enable hidden" data-which="A" type="checkbox" ${this.enableZoneA && 'checked'}>
                    </span>
                    <span style="color: grey;">Inner Zone</span>
                  </label>
                  <div data-controller="radial-color-field" style="height: fit-content;height: 24px;">
                    <input class="mct-zone-color" data-which="A" type="hidden" name="color" value="${this.zoneAColor}" data-radial-color-field-target="input">
                    <div data-radial-color-field-target="trigger" data-action="pointerdown->radial-color-field#open" class="radial-color-trigger" role="button" tabindex="0" aria-label="Pick color"></div>
                  </div>
                </div>

                <div class="mct-zone-row">
                  <label for="radiusA-${this.id}" class="mct-zone-field-label" data-controller="drag-number" data-drag-number-step-value="0.1">
                    <div class="mct-zone-field-name">RADIUS (<span class="mct-numerical-unit mct-zone-numerical-unit">${this.zoneRadiusUnit}</span>)</div>
                    <input id="radiusA-${this.id}" class="mct-zone-radius" data-which="A" type="number" step="0.1" min="0" value="${this.zoneARadius}" ${state.calibration || true ? '' : 'disabled'} max="${this.maxOuterRadius}">
                  </label>

                  <label for="rssiA-${this.id}" data-controller="drag-number" class="mct-zone-field-label ${state.visibleRSSI ? '' : 'hidden'}">
                    <div class="mct-zone-field-name">RSSI</div>
                    <input class="mct-zone-rssi" data-which="A" id="rssiA-${this.id}" step="-1" min="-110" max="-45" type="number" value="${this.rssiA}">
                  </label>
                </div>
              </div>

              <div class="mct-zone-range-container" style="--bg-color:${this.zoneBColor};">
                <div class="mct-sensor-zone-header">
                  <label class="mct-zone-name" for="enabledZoneB-${this.id}" style="font-size: 9px;">
                    <span class="mct-zone-name-span" style="display: block;margin-bottom: 4px;">Max Passing Signal Strength
                      <input id="enabledZoneB-${this.id}" class="mct-zone-enable hidden" data-which="B" type="checkbox" ${this.enableZoneB && 'checked'}>
                    </span>
                    <span style="color: grey;">Outer Zone</span>
                  </label>
                  <div data-controller="radial-color-field" style="height: fit-content;height: 24px;">
                    <input class="mct-zone-color" data-which="B" type="hidden" name="color" value="${this.zoneBColor}" data-radial-color-field-target="input">
                    <div data-radial-color-field-target="trigger" data-action="pointerdown->radial-color-field#open" class="radial-color-trigger" role="button" tabindex="0" aria-label="Pick color"></div>
                  </div>
                </div>

                <div class="mct-zone-row">
                  <label for="radiusB-${this.id}" class="mct-zone-field-label" data-controller="drag-number" data-drag-number-step-value="0.1">
                    <div class="mct-zone-field-name">RADIUS (<span class="mct-numerical-unit mct-zone-numerical-unit">${this.zoneRadiusUnit}</span>)</div>
                    <input id="radiusB-${this.id}" class="mct-zone-radius" data-which="B" type="number" step="0.1" min="0" value="${this.zoneBRadius}" ${state.calibration || true ? '' : 'disabled'} max="${this.maxOuterRadius}">
                  </label>

                  <label for="rssiB-${this.id}" data-controller="drag-number" class="mct-zone-field-label ${state.visibleRSSI ? '' : 'hidden'}">
                    <div class="mct-zone-field-name">RSSI</div>
                    <input class="mct-zone-rssi" data-which="B" id="rssiB-${this.id}" step="-1" min="-110" max="-45" type="number" value="${this.rssiB}">
                  </label>
                </div>
              </div>

            </div>
        </div>
    `
  }
}
